// ApiContext.tsx
import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import type { PDIProcedureApiManager } from './adapter/PDIProcedureApiAdapter';
import { ResultObj } from './Domain';

interface ApiProviderProps {
    children: ReactNode;
}

type ApiManagers = {
    [key: string]: any;
    PDIProcedureApiManager?: PDIProcedureApiManager;
};

export interface ApiContextProps {
    apiManagers: ApiManagers;
    registerApiManager: (name: string, manager: any) => Promise<void>;

    /** 存取暫存空間資料 
     * @param apiName example: "CarCreate"
     * @param data 有給則為儲存, 沒給則為取得
     */
    handleTemporaryStorageOperation: (apiName: string, data?: any) => Promise<ResultObj>
    temporaryStorage: { [apiName: string]: any; }
}

/** 訂閱最新 context 內容
 * @description 提供給 一般 class (或非 function component) 訂閱最新 context 內容
 * @author Olar */
class ApiContextSubscriber<T> {
    /** 儲存所有訂閱者, 並回傳目前最新 apiContext */
    private subscribers: Set<(context: T) => void>;
    /** 儲存目前最新 apiContext */
    private currentApiContext: T | null

    constructor() {
        this.subscribers = new Set();
        this.currentApiContext = null;
    }

    /** 訂閱方法 */
    subscribe(callBack: (context: T) => void): () => void {
        this.subscribers.add(callBack);
        // 若目前 context 存在
        if (this.currentApiContext !== null) {
            callBack(this.currentApiContext)
        } else {
            console.error('遺失最新版本 api context')
        }
        // 取消訂閱
        return () => {
            this.subscribers.delete(callBack);
        };
    }

    // 更新 api ocntext
    update(newApiContext: T): void {
        this.currentApiContext = newApiContext
        // 通知所有訂閱者
        this.subscribers.forEach(callback => callback(newApiContext));
    }
}

/** 建立一個 apiContextSubscriber 本人 */
export const apiContextSubscriber = new ApiContextSubscriber<ApiContextProps>()

export const ApiContext = createContext<ApiContextProps | undefined>(undefined);

export const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
    // apiManager 們
    const [apiManagers, setApiManagers] = useState<ApiManagers>({});
    // 暫存空間 
    const [temporaryStorage, setTemporaryStorage] = useState<{ [apiName: string]: any }>({
    });

    const registerApiManager = async (name: string, manager: any) => {
        setApiManagers((prev) => ({ ...prev, [name]: manager }));
    };

    /** 存取暫存空間資料 
     * @param apiName example: "CarCreate"
     * @param data 有給則為儲存, 沒給則為取得
     * 
    */
    const handleTemporaryStorageOperation = async (apiName: string, data?: any) => {
        let result: ResultObj = {
            message: '',
            success: true,
            data: null
        }
        if (data) {
            setTemporaryStorage(prev => {
                const newStorage = { ...prev, [apiName]: data };
                return newStorage;
            });
            result.message = `存入資料成功`;
        } else {
            result.data = temporaryStorage[apiName];
            result.message = `取出${apiName}資料${temporaryStorage[apiName] ? '成功' : '失敗'}`;
            result.success = !!temporaryStorage[apiName];
        }
        return result;
    }

    // 當有任何狀態異動時 主動更新訂閱者的context 內容 
    useEffect(() => {
        apiContextSubscriber.update({ apiManagers, registerApiManager, handleTemporaryStorageOperation, temporaryStorage })
    }, [temporaryStorage, apiManagers])

    return (
        <ApiContext.Provider value={{ apiManagers, registerApiManager, handleTemporaryStorageOperation, temporaryStorage }}>
            {children}
        </ApiContext.Provider>
    );
};

export function useApi<T = any>(name: string): T {
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error('useApi must be used within an ApiProvider');
    }
    return context.apiManagers[name];
}

